<template>
  <div class="irp-open-opportunities-cell">
    <RouterLink
      v-if="opportunityCount && opportunitiesRoute"
      :to="opportunitiesRoute"
      :aria-label="linkLabel"
      class="irp-open-opportunities-cell__link"
      @click="trackOpenOpportunities"
    >
      {{ opportunityCount }}
    </RouterLink>
    <span v-else class="irp-open-opportunities-cell__empty">
      {{ opportunityCount }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { IRP_CTAS } from '@crossbeam/itly';

import type { ICellRendererParams } from '@ag-grid-community/core';
import { pluralize } from 'humanize-plus';
import { computed } from 'vue';

import type { RecordOverviewPartnerType } from '@/types/individual_record';

import {
  EXPAND_ALL_QUERY,
  IRP_TAB,
  IRP_TAB_ROUTES,
  openOpportunityCount,
} from './irp';
import { useIRPTracking } from './useIRPTracking';
import { useRecordRouteParams } from './useRecordRouteParams';

const { params } = defineProps<{
  params: ICellRendererParams<RecordOverviewPartnerType>;
}>();

const recordParams = useRecordRouteParams();
const { trackIRPInteraction } = useIRPTracking();

const opportunityCount = computed(() =>
  openOpportunityCount(params.data?.opportunities?.items ?? []),
);

const partnerName = computed(() => params.data?.partner_name ?? '');

const opportunitiesRoute = computed(() => {
  const partnerOrgId = params.data?.partner_organization_id;
  if (!partnerOrgId) return null;
  return {
    name: IRP_TAB_ROUTES[IRP_TAB.opportunities],
    params: recordParams.value,
    query: { partner_org_ids: String(partnerOrgId), ...EXPAND_ALL_QUERY },
  };
});

function trackOpenOpportunities() {
  trackIRPInteraction({
    cta: IRP_CTAS.OPEN_OPPORTUNITIES,
    partner_org_id: String(params.data?.partner_organization_id),
    metadata: { opportunity_count: opportunityCount.value },
  });
}

const linkLabel = computed(() => {
  const opportunities = pluralize(
    opportunityCount.value,
    'opportunity',
    'opportunities',
  );
  return `View ${opportunityCount.value} open ${opportunities} with ${partnerName.value}`;
});
</script>

<style lang="pcss">
@reference "../../../css/theme.css";
.irp-open-opportunities-cell {
  @apply flex items-center h-full w-full text-sm;
}

.irp-open-opportunities-cell__link {
  @apply font-bold text-secondary-text rounded-4;
}

.irp-open-opportunities-cell__link:hover,
.irp-open-opportunities-cell__link:focus {
  @apply text-primary-text-link-hover;
}

.irp-open-opportunities-cell__link:focus-visible {
  @apply outline-hidden outline-2 outline-neutral-border-focus;
}

.irp-open-opportunities-cell__empty {
  @apply text-neutral-text-weak;
}
</style>
